import {
  ButtonGroup,
  Loading,
  Panel,
  Tooltip,
} from "@appkit4/react-components";
import { GridCellKind } from "@glideapps/glide-data-grid";
import "@glideapps/glide-data-grid/dist/index.css";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import CompanyPanel from "../components/CompanyPanel";
import toast from "../components/DismissibleToast";
import { BackButton, NextButton } from "../components/ProgressNav";
import {
  Button,
  DataEditor,
  InputNumber,
  Switch,
} from "../components/ReadonlyAwareInputs";
import RoundedBorder from "../components/ui/RoundedBorder";
import { useAuth } from "../services/AuthProvider";
import {
  calculate_reactivation_subtotals,
  getReviewCalc4Values,
} from "../services/calculations/calc4";
import { getBFDTotal } from "../services/calculations/ComponentTotals";
import {
  canProgressCalculation,
  disableProgressInViewMode,
  getCurrentPeriodIsReadonly,
  getGroupData,
  getGroupID,
  getPoaIndex,
  getSelectedPeriod,
  setGroupData,
} from "../services/GroupContext";
import sqldatabaseService from "../services/sqldatabase/sqldatabase.service";
import { getIncludedAdjCaps, sortCaps } from "../utils/adjustedCapsUtil";
import { processDateJS } from "../utils/dateProcessor";
import ReviewCalcNav from "./revCalcNavigation";
import RevCalcHeader from "./ReviewCalcHeader";
dayjs.extend(utc);

const ReviewCalc4 = () => {
  const auth = useAuth();
  const isReadOnlyOrViewPoA =
    auth?.isReadOnlyUser() || getCurrentPeriodIsReadonly();
  const disableViewModeProgress = disableProgressInViewMode();
  const tenantId = useSelector((state) => state.usageInfo.tenantID);

  const [isLoading, setIsLoading] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [isError, setIsError] = useState("");

  const savedData = getGroupData();
  const savedPOAData = getSelectedPeriod();
  const poaIndex = getPoaIndex();

  const [shouldZeroRemainingAllocation, setShouldZeroRemainingAllocation] =
    useState(savedPOAData?.shouldZeroRemainingAllocation ?? false);

  const [comp_reactivation_table_data, set_comp_reactivation_table_data] =
    useState([]);
  const [comp_reactivation_table_columns, set_comp_reactivation_table_columns] =
    useState([]);
  const [comp_reactivation_totals_column, set_comp_reactivation_totals_column] =
    useState([]);
  const comp_reactivation_table_rows = [
    {
      metric_id: "interest_reactivation_cap_gbp",
      display_name: "Company Interest reactivation cap - GBP",
      mandatory: true,
      type: "number",
    },
    {
      metric_id: "amount_available_for_reactivation_gbp",
      display_name: "Amount available for reactivation - GBP",
      mandatory: true,
      type: "number_total",
    },
    {
      metric_id: "allocated_reactivation",
      display_name: "Reactivation allocation - GBP",
      mandatory: true,
      editable: true,
      type: "number_total",
      default: 0,
    },
  ];

  const [initialSetupComplete, setInitialSetupComplete] = useState(false);

  const getCompReactivationsRowIndex = (metricId) =>
    comp_reactivation_table_rows.findIndex(
      (metric) => metric.metric_id === metricId,
    );

  if (
    getCompReactivationsRowIndex("amount_available_for_reactivation_gbp") ===
      -1 ||
    getCompReactivationsRowIndex("allocated_reactivation") === -1
  ) {
    throw new Error(
      "Error: comp table is missing Available or Allocated reactivation metrics",
    );
  }

  const [remainingCompReactivation, setRemainingCompReactivation] = useState(0);

  const [ap_reactivation_table_data, set_ap_reactivation_table_data] = useState(
    [],
  );
  const [ap_reactivation_table_columns, set_ap_reactivation_table_columns] =
    useState([]);
  const [ap_reactivation_remaining_row, set_ap_reactivation_remaining_row] =
    useState([]);
  const ap_reactivation_table_rows = [
    {
      metric_id: "start_date",
      display_name: "Accounting Period Start Date",
      mandatory: true,
      type: "date",
    },
    {
      metric_id: "end_date",
      display_name: "Accounting Period End Date",
      mandatory: true,
      type: "date",
    },
    {
      metric_id: "allocated_reactivation",
      display_name: "Reactivation allocated to the company - GBP",
      mandatory: true,
      type: "number",
      default: 0,
    },
    {
      metric_id: "currency",
      display_name: "Currency",
      mandatory: true,
      type: "string",
    },
    {
      metric_id: "allocated_reactivation_lc",
      display_name: "Reactivation allocated to the company - Local Currency",
      mandatory: true,
      type: "number",
      default: 0,
    },
    {
      metric_id: "election_override_automatic_allocations",
      display_name:
        "Apply s380(3) election, to override the automatic allocation of reactivated amounts",
      mandatory: true,
      editable: true,
      type: "boolean",
    },

    {
      metric_id: "ntlr_reactivation_amount",
      display_name:
        "Non trading loan relationship tax-interest expense - available for reactivation",
      mandatory: true,
      type: "number",
    },
    {
      metric_id: "cpr_non_trading_loan_relationships",
      display_name: "Non trading loan relationship reactivated amount",
      mandatory: true,
      type: "edit_number",
    },
    {
      metric_id: "ntdc_reactivation_amount",
      display_name:
        "Non trading derivative contract tax-interest expense - available for reactivation",
      mandatory: true,
      type: "number",
    },
    {
      metric_id: "cpr_non_trading_derivatives",
      display_name: "Non trading derivative contract reactivated amount",
      mandatory: true,
      type: "edit_number",
    },
    {
      metric_id: "tlr_reactivation_amount",
      display_name:
        "Trading loan relationship tax-interest expense - available for reactivation",
      mandatory: true,
      type: "number",
    },
    {
      metric_id: "cpr_trading_loan_relationships",
      display_name: "Trading loan relationship reactivated amount",
      mandatory: true,
      type: "edit_number",
    },
    {
      metric_id: "tdc_reactivation_amount",
      display_name:
        "Trading derivative contract tax-interest expense - available for reactivation",
      mandatory: true,
      type: "number",
    },
    {
      metric_id: "cpr_trading_derivatives",
      display_name: "Trading derivative contract reactivated amount",
      mandatory: true,
      type: "edit_number",
    },
    {
      metric_id: "ofa_reactivation_amount",
      display_name:
        "Other financing arrangements tax-interest expense - available for reactivation",
      mandatory: true,
      type: "number",
    },
    {
      metric_id: "cpr_other_financing",
      display_name: "Other financing arrangements reactivated amount",
      mandatory: true,
      type: "edit_number",
    },
    {
      metric_id: "remaining_reactivation",
      display_name: "Remaining reactivation to be allocated",
      mandatory: true,
      type: "reactivation_total",
    },
  ];

  const allocationComponentsKeys = [
    "cpr_non_trading_loan_relationships",
    "cpr_non_trading_derivatives",
    "cpr_trading_loan_relationships",
    "cpr_trading_derivatives",
    "cpr_other_financing",
  ];

  const allocationComponentsKeyIndexMap = allocationComponentsKeys.reduce(
    (obj, key) => {
      obj[key] = ap_reactivation_table_rows.findIndex(
        (metric) => metric.metric_id === key,
      );
      return obj;
    },
    {},
  );

  for (let key in allocationComponentsKeyIndexMap) {
    if (allocationComponentsKeyIndexMap[key] === -1) {
      throw new Error(
        `Error: ap table is missing ${key} metric which is a component of reactivation`,
      );
    }
  }

  const getApReactivationsRowIndex = (metricId) =>
    ap_reactivation_table_rows.findIndex((row) => row.metric_id === metricId);

  if (
    getApReactivationsRowIndex("allocated_reactivation_lc") === -1 ||
    getApReactivationsRowIndex("remaining_reactivation") === -1 ||
    getApReactivationsRowIndex("election_override_automatic_allocations") === -1
  ) {
    throw new Error(
      "Error: ap table is missing Election, Allocated or Remaining reactivation metric",
    );
  }

  const totalBFD = getBFDTotal("bfd", savedPOAData);
  const totalBFDADJ = getBFDTotal("bfdadj", savedPOAData);
  const groupTotalBFDDisallowances = totalBFD + totalBFDADJ;

  let availReactivations = 0;
  if (
    comp_reactivation_table_data[
      getCompReactivationsRowIndex("amount_available_for_reactivation_gbp")
    ]
  ) {
    availReactivations = comp_reactivation_table_data[
      getCompReactivationsRowIndex("amount_available_for_reactivation_gbp")
    ].reduce((sum, a) => sum + a, 0);
  }

  useEffect(() => {
    if (!comp_reactivation_table_data.length || isReadOnlyOrViewPoA) {
      setIsError(false);
      return;
    }

    // Calculate Totals column
    const tmp_comp_reactivation_totals_column = [
      ...comp_reactivation_totals_column,
    ];
    comp_reactivation_table_rows.forEach((metric, index) => {
      if (metric.type !== "number_total") return;

      if (metric.metric_id === "amount_available_for_reactivation_gbp") {
        if (shouldZeroRemainingAllocation) {
          tmp_comp_reactivation_totals_column[index] = 0;
          return;
        }
      }

      tmp_comp_reactivation_totals_column[index] = comp_reactivation_table_data[
        index
      ].reduce((acc, val) => acc + (val || 0), 0);
    });
    set_comp_reactivation_totals_column(tmp_comp_reactivation_totals_column);

    // Set remaining reactivation
    setRemainingCompReactivation(
      Math.min(
        tmp_comp_reactivation_totals_column[
          getCompReactivationsRowIndex("amount_available_for_reactivation_gbp")
        ],
        Math.round(savedPOAData.group_interest_reactivation_cap),
      ) -
        tmp_comp_reactivation_totals_column[
          getCompReactivationsRowIndex("allocated_reactivation")
        ],
    );

    const allValid = comp_reactivation_table_data[
      getCompReactivationsRowIndex("allocated_reactivation")
    ]
      .map((value) => (isNaN(value) ? 0 : value))
      .every((value, index) => {
        value = value || 0;
        return (
          value >= 0 &&
          value <=
            (comp_reactivation_table_data[
              getCompReactivationsRowIndex(
                "amount_available_for_reactivation_gbp",
              )
            ][index] || 0)
        );
      });
    if (!allValid) {
      setIsError(
        "Table Error: Reactivation allocation must be a number greater than 0 and less than the amount available for reactivation",
      );
    } else {
      setIsError("");
    }

    // Carry Comp allocations down to AP table
    let temp_ap_reactivation_data = [...ap_reactivation_table_data];
    temp_ap_reactivation_data[
      getApReactivationsRowIndex("allocated_reactivation")
    ].forEach((_, index) => {
      // const fxRate = getSelectedPeriod()?.adjusted_caps && getSelectedPeriod()?.adjusted_caps[index]?.sort(cap => cap.company_name).fx_rate;
      const selectedPeriod = getSelectedPeriod();
      const selectedCap = selectedPeriod?.adjusted_caps?.find(
        (cap) =>
          cap.company_name ===
            ap_reactivation_table_columns[index + 1]?.title &&
          processDateJS(cap.start_date).isSame(
            processDateJS(
              temp_ap_reactivation_data[
                getApReactivationsRowIndex("start_date")
              ][index],
            ),
          ) &&
          processDateJS(cap.end_date).isSame(
            processDateJS(
              temp_ap_reactivation_data[getApReactivationsRowIndex("end_date")][
                index
              ],
            ),
          ),
      );
      const fxRate = selectedCap?.fx_rate;

      const reactivationsAllocated =
        comp_reactivation_table_data[
          getCompReactivationsRowIndex("allocated_reactivation")
        ][index];
      temp_ap_reactivation_data[
        getApReactivationsRowIndex("allocated_reactivation")
      ][index] = reactivationsAllocated;
      temp_ap_reactivation_data[
        getApReactivationsRowIndex("allocated_reactivation_lc")
      ][index] = Math.min(
        Math.round(reactivationsAllocated * fxRate),
        selectedCap?.bfd_subtotal + selectedCap?.bfdadj_subtotal,
      );
    });

    ap_reactivation_table_data?.[0]?.forEach((_, col) => {
      if (
        ap_reactivation_table_data[
          getApReactivationsRowIndex("election_override_automatic_allocations")
        ][col] === false
      ) {
        const cap = savedPOAData?.adjusted_caps?.find(
          (cap) =>
            cap?.company_name ===
              ap_reactivation_table_columns[col + 1]?.title &&
            processDateJS(cap?.start_date)?.isSame(
              processDateJS(temp_ap_reactivation_data[0][col]),
            ),
        );

        if (cap && !isReadOnlyOrViewPoA) {
          const allocations = calculate_reactivation_subtotals(
            cap,
            temp_ap_reactivation_data[
              getApReactivationsRowIndex("allocated_reactivation_lc")
            ][col],
          );
          temp_ap_reactivation_data = applyAPAllocation(
            temp_ap_reactivation_data,
            col,
            allocations,
          );
        }
      }
    });

    set_ap_reactivation_table_data(temp_ap_reactivation_data);
  }, [comp_reactivation_table_data, shouldZeroRemainingAllocation]);

  useEffect(() => {
    if (!ap_reactivation_table_data?.length) return;

    let temp_ap_reactivation_remaining_row = [...ap_reactivation_remaining_row];
    ap_reactivation_table_data[
      getApReactivationsRowIndex("remaining_reactivation")
    ].forEach((_, index) => {
      temp_ap_reactivation_remaining_row[index] = shouldZeroRemainingAllocation
        ? 0
        : getRemainingAPAllocation(ap_reactivation_table_data, index);
    });
    set_ap_reactivation_remaining_row(temp_ap_reactivation_remaining_row);
  }, [ap_reactivation_table_data]);

  // get calculated values
  useEffect(() => {
    const savedData = getGroupData();
    if (poaIndex > -1) {
      if (isReadOnlyOrViewPoA) {
        setupTableData(savedData);
        return;
      }
      const poa = getReviewCalc4Values(
        savedData?.periods_of_account[poaIndex],
        savedData,
      );
      if (poa) {
        savedData.periods_of_account[poaIndex] = poa;
        setupTableData(savedData);
        setGroupData(savedData);

        const zeroRemainingAllocationValue =
          poa?.apply_NARRC2 || (poa.shouldZeroRemainingAllocation ?? false);
        setShouldZeroRemainingAllocation(zeroRemainingAllocationValue);
      }
    } else {
      toast.error(`could not find POA data`, {
        duration: 30 * 1000, // 30s
      });
    }

    setInitialSetupComplete(true);
  }, []);

  const setupTableData = (calculationResult) => {
    const savedPOAData =
      calculationResult?.periods_of_account[poaIndex > -1 ? poaIndex : 0];

    const adjustedCaps = sortCaps(
      getIncludedAdjCaps(savedPOAData?.adjusted_caps),
    );
    const isReviewCalc4Saved = adjustedCaps?.some((cap) =>
      cap?.hasOwnProperty("allocated_reactivation"),
    );

    const extractRowValue = (rowHeader, cap) => {
      const reactivationMetricsMap = {
        ntlr_reactivation_amount: [
          "bfd_non_trading_loan_relationships",
          "bfdadj_non_trading_loan_relationships",
        ],
        ntdc_reactivation_amount: [
          "bfd_non_trading_derivatives",
          "bfdadj_non_trading_derivatives",
        ],
        tlr_reactivation_amount: [
          "bfd_trading_loan_relationships",
          "bfdadj_trading_loan_relationships",
        ],
        tdc_reactivation_amount: [
          "bfd_trading_derivatives",
          "bfdadj_trading_derivatives",
        ],
        ofa_reactivation_amount: [
          "bfd_other_financing",
          "bfdadj_other_financing",
        ],
      };
      if (
        rowHeader.hasOwnProperty("default") &&
        !isReviewCalc4Saved &&
        !isReadOnlyOrViewPoA
      ) {
        return rowHeader.default;
      } else if (rowHeader?.type === "calculation") {
        return Number(rowHeader?.calculation(cap)).toLocaleString();
      } else if (rowHeader?.type?.includes("blank")) {
        return "";
      } else if (cap?.hasOwnProperty(rowHeader?.metric_id)) {
        if (cap[rowHeader?.metric_id] === "No value") {
          return 0;
        } else {
          return cap[rowHeader?.metric_id];
        }
      } else if (
        rowHeader.metric_id === "election_override_automatic_allocations"
      ) {
        return false;
      } else if (
        Object.keys(reactivationMetricsMap).includes(rowHeader.metric_id) &&
        isReadOnlyOrViewPoA
      ) {
        const metrics = reactivationMetricsMap[rowHeader.metric_id];
        return metrics.reduce((sum, metric) => sum + cap?.[metric], 0);
      }

      return "";
    };

    const populateTableData = (tableRows) => {
      let tempTableDataBuilder = [];

      tableRows?.forEach((rowHeader) => {
        let tempRowDataBuilder = [];
        adjustedCaps?.forEach((cap, index) => {
          const capCompanyID =
            index > 0 ? adjustedCaps[index - 1]?.company_ID : null;
          if (index === 0 || cap?.company_ID !== capCompanyID) {
            const val = extractRowValue(rowHeader, cap);
            tempRowDataBuilder.push(val);
          }
        });
        tempTableDataBuilder?.push(tempRowDataBuilder);
      });

      return tempTableDataBuilder;
    };

    // Populate company reactivation table
    let temp_comp_reactivation_table_columns = [
      { title: "", width: 200 },
      { title: "Group Total", width: 200 },
    ];
    adjustedCaps?.forEach((cap, index) => {
      const prevComp = index > 0 ? adjustedCaps[index - 1]?.company_ID : null;
      if (index === 0 || cap?.company_ID !== prevComp) {
        const companyName = calculationResult?.companies?.filter(
          (c) => c?.company_ID === cap?.company_ID,
        )[0]["company_name"];
        temp_comp_reactivation_table_columns?.push({
          title: companyName,
          width: 200,
        });
        // also set default allocated_reactivation value
        adjustedCaps[index].allocated_reactivation =
          adjustedCaps[index]?.allocated_reactivation &&
          !isNaN(adjustedCaps[index]?.allocated_reactivation)
            ? adjustedCaps[index]?.allocated_reactivation
            : 0;
        adjustedCaps[index].allocated_reactivation_lc = Math.min(
          Math.round(
            adjustedCaps[index]?.allocated_reactivation *
              adjustedCaps[index]?.fx_rate,
          ),
          adjustedCaps[index]?.bfd_subtotal ||
            adjustedCaps[index].bfdadj_subtotal,
        );
      }
    });
    set_comp_reactivation_table_columns(temp_comp_reactivation_table_columns);

    let tempTableDataBuilder = populateTableData(comp_reactivation_table_rows);
    set_comp_reactivation_table_data(tempTableDataBuilder);
    set_comp_reactivation_totals_column(
      new Array(tempTableDataBuilder[0].length).fill(0),
    );

    // Populate reactivation table
    let temp_ap_reactivation_table_columns = [{ title: "", width: 400 }];
    adjustedCaps?.forEach((cap, index) => {
      const prevCompId = index > 0 ? adjustedCaps[index - 1]?.company_ID : null;
      if (index === 0 || cap?.company_ID !== prevCompId) {
        const companyName = calculationResult?.companies?.filter(
          (c) => c?.company_ID === cap?.company_ID,
        )[0]["company_name"];
        temp_ap_reactivation_table_columns?.push({
          title: companyName,
          width: 200,
        });
      }
    });
    set_ap_reactivation_table_columns(temp_ap_reactivation_table_columns);

    tempTableDataBuilder = populateTableData(ap_reactivation_table_rows);
    set_ap_reactivation_table_data(tempTableDataBuilder);
    set_ap_reactivation_remaining_row(
      new Array(tempTableDataBuilder[0].length).fill(0),
    );
  };

  const getCompCellContent = useCallback(
    (col, row) => {
      const metric = comp_reactivation_table_rows[row];
      if (metric?.type === "blank") {
        return {
          kind: GridCellKind.Text,
          allowOverlay: false,
          data: " ",
          displayData: " ",
          span: [0, savedData?.length],
        };
      }

      if (
        metric?.type === "header" ||
        metric?.type === "subheader" ||
        metric?.type === "note"
      ) {
        return {
          kind: GridCellKind.Text,
          allowOverlay: false,
          data: metric?.display_name,
          displayData: metric?.display_name,
          span: [0, savedData?.length],
        };
      }

      if (col === -2) {
        // Metric name
        return {
          kind: GridCellKind.Text,
          data: metric?.display_name,
          allowOverlay: false,
          displayData: metric?.display_name,
          allowWrapping: true,
        };
      } else if (col === -1) {
        // Totals column
        if (metric?.type?.includes("number_total")) {
          return {
            kind: GridCellKind.Number,
            data: comp_reactivation_totals_column[row],
            displayData: comp_reactivation_totals_column[row]
              ? Math?.round(
                  comp_reactivation_totals_column[row],
                )?.toLocaleString()
              : "0",
          };
        }
        return {
          kind: GridCellKind.Text,
          allowOverlay: false,
          data: " ",
          displayData: " ",
          span: [0, savedData?.length],
        };
      }

      // Default values
      let cellType = GridCellKind.Text;
      let value = comp_reactivation_table_data[row][col];
      let displayValue = value ? String(value) : "0";
      let editable = metric?.editable ?? false;

      if (metric?.type?.includes("date")) {
        value = displayValue = value
          ? processDateJS(value)?.format("DD/MM/YYYY")
          : "";
      } else if (metric?.type?.includes("number")) {
        cellType = GridCellKind.Number;
        displayValue = value ? Math?.round(value)?.toLocaleString() : "0";
      } else if (metric?.type?.includes("boolean")) {
        cellType = GridCellKind.Boolean;
        if (value === 1 || value === true) {
          value = displayValue = true;
        } else if (value === 0 || value === false) {
          value = displayValue = false;
        } else {
          value = displayValue = false;
        }
      }

      if (metric.metric_id === "amount_available_for_reactivation_gbp") {
        if (shouldZeroRemainingAllocation) {
          value = 0;
          displayValue = "0";
        }
      }

      return {
        kind: cellType,
        data: value,
        allowOverlay: editable,
        displayData: displayValue,
        themeOverride: {
          ...(metric?.style === "bold" && { baseFontStyle: "600 15px" }),
          ...(editable ? { bgCell: "#FFFFFF" } : { bgCell: "#ffd5b7" }),
        },
      };
    },
    [
      comp_reactivation_table_data,
      comp_reactivation_table_rows,
      comp_reactivation_totals_column,
    ],
  );

  const getAPCellContent = useCallback(
    (col, row) => {
      const metric = ap_reactivation_table_rows[row];
      if (metric?.type === "blank") {
        return {
          kind: GridCellKind.Text,
          allowOverlay: false,
          data: " ",
          displayData: " ",
          span: [0, savedData?.length],
        };
      }

      if (
        metric?.type === "header" ||
        metric?.type === "subheader" ||
        metric?.type === "note"
      ) {
        return {
          kind: GridCellKind.Text,
          allowOverlay: false,
          data: metric?.display_name,
          displayData: metric?.display_name,
          span: [0, savedData?.length],
        };
      }

      // Metric name
      if (col === -1) {
        return {
          kind: GridCellKind.Text,
          data: metric?.display_name,
          allowOverlay: false,
          displayData: metric?.display_name,
          allowWrapping: true,
        };
      }

      // set up default values
      let cellType = GridCellKind.Text;
      let value = ap_reactivation_table_data[row][col];
      let displayValue = value ? String(value) : "0";
      let editable = metric?.editable ?? false;

      if (metric?.metric_id === "remaining_reactivation") {
        cellType = GridCellKind.Number;
        value = ap_reactivation_remaining_row[col];
        displayValue = ap_reactivation_remaining_row[col]
          ? Math?.round(ap_reactivation_remaining_row[col])?.toLocaleString()
          : "0";
        editable = false;
      }

      // if cell is only editable on selection of a checkbox
      if (metric?.type?.includes("edit_number")) {
        cellType = GridCellKind.Number;
        editable =
          ap_reactivation_table_data[
            getApReactivationsRowIndex(
              "election_override_automatic_allocations",
            )
          ][col];
        displayValue = value ? Math?.round(value)?.toLocaleString() : "0";
      } else if (metric?.type?.includes("date")) {
        value = displayValue = value
          ? processDateJS(value)?.format("DD/MM/YYYY")
          : "";
      } else if (metric?.type?.includes("number")) {
        cellType = GridCellKind?.Number;
        displayValue = value ? Math.round(value)?.toLocaleString() : "0";
      } else if (metric?.type?.includes("boolean")) {
        cellType = GridCellKind.Boolean;
        if (value === 1 || value === true) {
          value = displayValue = true;
        } else if (value === 0 || value === false) {
          value = displayValue = false;
        } else {
          value = displayValue = false;
        }
      }
      return {
        kind: cellType,
        data: value,
        allowOverlay: editable,
        displayData: displayValue,
        themeOverride: {
          ...(metric?.style === "bold" && { baseFontStyle: "600 15px" }),
          ...(editable ? { bgCell: "#FFFFFF" } : { bgCell: "#ffd5b7" }),
        },
      };
    },
    [
      ap_reactivation_table_data,
      ap_reactivation_table_rows,
      ap_reactivation_remaining_row,
    ],
  );

  const [rowGrouping, setRowGrouping] = useState(() => ({
    groups: [
      {
        headerIndex: 0,
        isCollapsed: false,
      },
      {
        headerIndex: 10,
        isCollapsed: true,
        subGroups: [
          {
            headerIndex: 15,
            isCollapsed: false,
          },
          {
            headerIndex: 20,
            isCollapsed: false,
          },
        ],
      },
      {
        headerIndex: 30,
        isCollapsed: false,
      },
    ],
    height: 55,
    navigationBehavior: "block",
    selectionBehavior: "block-spanning",
    themeOverride: {
      bgCell: "rgba(0, 100, 255, 0.1)",
    },
  }));

  const onCompCellEdit = (col, row, value) => {
    const metric = comp_reactivation_table_rows[row];
    if (!metric) return;

    let temp_comp_reactivation_data = [...comp_reactivation_table_data];

    if (metric?.type === "date") {
      const dayjsDate = processDateJS(value)?.format("DD/MM/YYYY");
      if (!dayjsDate?.isValid()) {
        toast.error("Date must be entered in dd/mm/yyyy format");
        return;
      }
      value = dayjsDate?.toDate();
    } else if (metric?.type?.includes("number")) {
      value = isNaN(value) ? 0 : Math.round(value);
    }

    temp_comp_reactivation_data[row][col] = value || 0;

    set_comp_reactivation_table_data(temp_comp_reactivation_data);
  };

  const onAPCellEdit = (col, row, value) => {
    const metric = ap_reactivation_table_rows[row];
    if (!metric) {
      return;
    }

    let temp_ap_reactivation_data = [...ap_reactivation_table_data];
    if (metric?.type === "date") {
      const dayjsDate = processDateJS(value)?.format("DD/MM/YYYY");
      if (!dayjsDate?.isValid()) {
        toast.error("Date must be entered in dd/mm/yyyy format");
        return;
      }
      value = dayjsDate?.toDate();
    } else if (metric?.type === "boolean") {
      temp_ap_reactivation_data[
        getApReactivationsRowIndex("election_override_automatic_allocations")
      ][col] = value;
    } else if (metric?.type?.includes("number")) {
      value = isNaN(value) ? 0 : Math.round(value);
    }

    temp_ap_reactivation_data[row][col] = value;

    if (
      metric?.metric_id === "election_override_automatic_allocations" &&
      value === false
    ) {
      const cap = savedPOAData?.adjusted_caps?.find(
        (cap) =>
          cap?.company_name === ap_reactivation_table_columns[col + 1]?.title &&
          processDateJS(cap?.start_date)?.isSame(
            processDateJS(temp_ap_reactivation_data[0][col]),
          ),
      );
      if (cap) {
        const allocations = calculate_reactivation_subtotals(
          cap,
          temp_ap_reactivation_data[
            getApReactivationsRowIndex("allocated_reactivation_lc")
          ][col],
        );
        temp_ap_reactivation_data = applyAPAllocation(
          temp_ap_reactivation_data,
          col,
          allocations,
        );
      }
    }

    set_ap_reactivation_table_data(temp_ap_reactivation_data);
  };

  const getMetricTheme = (metric) => {
    if (!metric) {
      return {};
    }

    if (metric?.type === "blank") {
      return {
        bgCell: "#F4F3F7",
      };
    }

    if (metric?.type === "header") {
      return {
        baseFontStyle: "600 18px",
      };
    }

    if (metric?.type === "subheader") {
      return {
        baseFontStyle: "600 15px",
      };
    }

    if (metric?.type === "note") {
      return {
        baseFontStyle: "600 12px",
      };
    }
  };

  const applyAutomaticAllocation = () => {
    let temp_comp_table_data = [...comp_reactivation_table_data];
    let temp_ap_table_data = [...ap_reactivation_table_data];

    temp_comp_table_data[0]?.forEach((_, index) => {
      temp_comp_table_data[
        getCompReactivationsRowIndex("allocated_reactivation")
      ][index] =
        temp_comp_table_data[
          getCompReactivationsRowIndex("amount_available_for_reactivation_gbp")
        ][index];

      const zeroedAllocation = allocationComponentsKeys.reduce((obj, key) => {
        obj[key] = 0;
        return obj;
      }, {});

      temp_ap_table_data = applyAPAllocation(
        temp_ap_table_data,
        index,
        zeroedAllocation,
      );
    });

    set_comp_reactivation_table_data(temp_comp_table_data);
    set_ap_reactivation_table_data(temp_ap_table_data);
  };

  const resetAllocation = () => {
    let temp_comp_table_data = [...comp_reactivation_table_data];
    let temp_ap_table_data = [...ap_reactivation_table_data];

    temp_comp_table_data[0]?.forEach((_, index) => {
      temp_comp_table_data[
        getCompReactivationsRowIndex("allocated_reactivation")
      ][index] = 0;

      const zeroedAllocation = allocationComponentsKeys.reduce((obj, key) => {
        obj[key] = 0;
        return obj;
      }, {});

      temp_ap_table_data = applyAPAllocation(
        temp_ap_table_data,
        index,
        zeroedAllocation,
      );
    });

    set_comp_reactivation_table_data(temp_comp_table_data);
    set_ap_reactivation_table_data(temp_ap_table_data);
  };

  const applyAPAllocation = (temp_ap_reactivation_data, col, newValues) => {
    Object.keys(allocationComponentsKeyIndexMap).forEach((k) => {
      temp_ap_reactivation_data[allocationComponentsKeyIndexMap[k]][col] =
        newValues[k];
    });

    return temp_ap_reactivation_data;
  };

  const getRemainingAPAllocation = (ap_table_data, col) => {
    let total =
      ap_table_data[getApReactivationsRowIndex("allocated_reactivation_lc")][
        col
      ];

    Object.keys(allocationComponentsKeyIndexMap).forEach((key) => {
      total -= Math.round(
        ap_table_data[allocationComponentsKeyIndexMap[key]][col],
      );
    });

    return total;
  };

  const save = async () => {
    if (isError) {
      throw "There is an error. Please fix before proceeding...";
    }

    if (isReadOnlyOrViewPoA) {
      return;
    }

    setIsPosting(true);

    const poaEndDate = savedData.periods_of_account[poaIndex].period_end;

    if (!savedData.periods_of_account[poaIndex].unused_allowances_for_group) {
      savedData.periods_of_account[poaIndex].unused_allowances_for_group = [];
    }

    // Remove existing entry in unused_allowances_for_group
    savedData.unused_allowances_for_group =
      savedData.unused_allowances_for_group.filter(
        (entry) => entry.period_end !== poaEndDate,
      );

    // Calculate new entry values
    const aggregateNetTaxInterestExpense =
      savedData.periods_of_account[poaIndex]
        .aggregate_net_tax_interest_expense || 0;
    const groupInterestAllowance =
      savedData.periods_of_account[poaIndex].group_interest_allowance || 0;
    const groupReactivation = Math.min(
      Math.round(savedPOAData.group_interest_reactivation_cap),
      Math.round(
        comp_reactivation_totals_column[
          getCompReactivationsRowIndex("amount_available_for_reactivation_gbp")
        ],
      ),
    );

    const unusedAllowance = shouldZeroRemainingAllocation
      ? 0
      : Math.max(
          groupInterestAllowance -
            aggregateNetTaxInterestExpense -
            groupReactivation,
          0,
        );

    const existingIndex = savedData.unused_allowances_for_group.findIndex(
      (entry) =>
        entry.period_start ===
        savedData.periods_of_account[poaIndex].period_start,
    );

    // Create the new entry
    const newUnusedAllowanceEntry = {
      aggregate_net_tax_interest_expense: aggregateNetTaxInterestExpense,
      interest_allowance: groupInterestAllowance,
      reactivations_prior_first_period: 0,
      unused_allowance: unusedAllowance,
      unused_generated: unusedAllowance,
      period_end: poaEndDate,
      period_start: savedData.periods_of_account[poaIndex].period_start,
    };

    // If an entry already exists, replace it
    if (existingIndex !== -1) {
      savedData.unused_allowances_for_group[existingIndex] =
        newUnusedAllowanceEntry;

      // If not, push the new entry
    } else {
      savedData.unused_allowances_for_group.push(newUnusedAllowanceEntry);
    }

    // Get full data of displayed caps
    // copied from calc4
    const displayed_caps = sortCaps(savedPOAData.adjusted_caps)?.filter(
      (cap, index) => {
        const prevComp =
          index > 0 ? savedPOAData?.adjusted_caps[index - 1]?.company_ID : null;
        return index === 0 || cap.company_ID !== prevComp;
      },
    );

    // Write data to new caps
    savedData.periods_of_account[poaIndex].adjusted_caps =
      savedPOAData.adjusted_caps.map((ap) => {
        const capIndex = displayed_caps.findIndex(
          (cap, index) =>
            cap.company_ID === ap.company_ID &&
            processDateJS(cap.end_date)?.isSame(processDateJS(ap.end_date)) &&
            processDateJS(cap.start_date)?.isSame(processDateJS(ap.start_date)),
        );

        return {
          reviewCalc4Saved: true,
          ...(capIndex !== -1
            ? {
                ...ap,
                ...comp_reactivation_table_rows.reduce(
                  (acc, metric, metricIndex) => {
                    acc[metric.metric_id] =
                      comp_reactivation_table_data[metricIndex][capIndex];
                    return acc;
                  },
                ),
                ...ap_reactivation_table_rows.reduce(
                  (acc, metric, metricIndex) => {
                    acc[metric.metric_id] =
                      ap_reactivation_table_data[metricIndex][capIndex];
                    return acc;
                  },
                ),
              }
            : ap),
        };
      });

    const canProgressCalc = canProgressCalculation();

    const currentPoA = savedData.periods_of_account[poaIndex];
    savedData.periods_of_account[poaIndex] = {
      ...currentPoA,
      shouldZeroRemainingAllocation,
      group_reactivations: groupReactivation,
      group_interest_reactivation_available: availReactivations,
      ...canProgressCalc,
    };

    const { unused_allowances_for_group, furthest_page_visited } = savedData;
    const postData = {
      id: getGroupID(),
      poa: savedData.periods_of_account[poaIndex],
      poaIndex,
      unusedAllowancesForGroup: unused_allowances_for_group,
      furthest_page_visited,
    };

    const promise = sqldatabaseService?.postReviewCalc24(tenantId, postData);

    const onSuccess = () => {
      setGroupData(savedData);
      setIsPosting(false);
    };
    const onError = () => setIsPosting(false);
    return toast.save(promise, onSuccess, onError);
  };

  useEffect(() => {
    if (
      !initialSetupComplete ||
      !shouldZeroRemainingAllocation ||
      isReadOnlyOrViewPoA
    )
      return;
    if (shouldZeroRemainingAllocation && !isReadOnlyOrViewPoA)
      resetAllocation();
  }, [shouldZeroRemainingAllocation, initialSetupComplete]);

  const validateReactivationAllocation = () => {
    if (isReadOnlyOrViewPoA) return true;

    return (
      Math.round(remainingCompReactivation)?.toLocaleString() === "0" &&
      ap_reactivation_remaining_row
        ?.map((apRemainingReactivation) =>
          isNaN(apRemainingReactivation) ? 0 : apRemainingReactivation,
        )
        .every((apRemainingReactivation) => apRemainingReactivation === 0)
    );
  };

  if (isLoading)
    return (
      <Loading
        loadingType="linear"
        indeterminate={true}
        compact={false}
        className="page-loader"
      ></Loading>
    );
  return (
    <div className="ap-container">
      <CompanyPanel />
      <br />
      <Panel>
        <RevCalcHeader groupname="Reactivation of interest" />
        <ReviewCalcNav currentStep={3} />
        <p>
          Where a group has an 'amount available for reactivation' in respect of
          a return period, this amount needs to be allocated among the companies
          in the group, subject to each company's interest reactivation cap.
        </p>
        <p className="col-7">
          Group Interest Reactivation Cap:{" "}
          <strong>
            {Math.round(
              savedPOAData.group_interest_reactivation_cap,
            )?.toLocaleString()}
          </strong>
        </p>
        <p className="col-7">
          Group total disallowances brought forward before application of
          company interest reactivation cap:{" "}
          <strong>
            {Math.round(groupTotalBFDDisallowances)?.toLocaleString()}
          </strong>
        </p>
        <p className="col-7">
          Group total amount available for reactivation:{" "}
          <strong>{availReactivations.toLocaleString()}</strong>
        </p>
        <p className="col-7">
          Reactivations to be allocated:{" "}
          <strong>
            {Math.min(
              Math.round(savedPOAData.group_interest_reactivation_cap),
              Math.round(
                comp_reactivation_totals_column[
                  getCompReactivationsRowIndex(
                    "amount_available_for_reactivation_gbp",
                  )
                ],
              ),
            )?.toLocaleString()}
          </strong>
        </p>
        <h2 style={{ marginTop: "3rem" }}>Company - Reactivation Allocation</h2>
        <p>
          Where an amount is available for reactivation in respect of the Period
          of Account of a Group, this needs to be allocated among the companies
          within the group, subject to each company's reactivation limit.
        </p>
        <p className="err">{isError}</p>
        <Switch
          className="mb-4 switch-xl"
          large
          disabled={savedPOAData?.apply_NARRC2}
          checked={shouldZeroRemainingAllocation}
          onChange={() => {
            setShouldZeroRemainingAllocation(!shouldZeroRemainingAllocation);
          }}
        >
          <p className="red-text bold no-margin">
            Toggle this box if you intend to finalise the calculation without a
            reporting company or make an abbreviated return. In these cases, no
            reactivations can be made in the period, and all allocations must be
            set to zero.
          </p>
        </Switch>
        <div
          id="portal"
          style={{ position: "fixed", left: 0, top: 0, zIndex: 9999 }}
        />
        <RoundedBorder>
          <DataEditor
            freezeColumns={1}
            columns={comp_reactivation_table_columns}
            getCellContent={([col, row]) =>
              getCompCellContent((col = col - 2), row)
            }
            rows={comp_reactivation_table_rows?.length}
            rowGrouping={rowGrouping}
            onCellEdited={([col, row], event) =>
              onCompCellEdit((col = col - 2), row, event?.data)
            }
            getRowThemeOverride={(i) =>
              getMetricTheme(comp_reactivation_table_rows[i])
            }
          />
        </RoundedBorder>
        <div className="row">
          <div className="col-3">Remaining reactivation to be allocated</div>
          <InputNumber
            key={remainingCompReactivation}
            className="col-auto"
            value={remainingCompReactivation?.toLocaleString() ?? 0} //Updated formatting to ensure that we have financial data format.
            readonly
          ></InputNumber>
          <ButtonGroup className="col-2" compact={false}>
            <Button onClick={applyAutomaticAllocation}>
              Apply Automatic Allocation
            </Button>
            <Button onClick={resetAllocation}>Reset</Button>
          </ButtonGroup>
        </div>
        <h2 style={{ marginTop: "3rem" }}>
          Accounting Period - Reactivation allocation
        </h2>
        <p className="mb-0">
          Where a company has an allocated reactivation amount in respect of the
          Period of Account of a Group, the reactivated tax-interest amounts are
          brought into account in the earliest relevant accounting period.
          <br />
          By default, tax-interest expense amounts must be brought into account
          in the following order:
        </p>
        <ol className="mb-2">
          <li>Non-trading debits in respect of loan relationships</li>
          <li>Non-trading debits in respect of derivative contracts</li>
          <li>
            Debits in respect of loan relationships treated as expenses of trade
          </li>
          <li>
            Debits in respect of derivative contracts treated as expenses of
            trade
          </li>
          <li>
            Finance leases, debt factoring and service concession arrangements
          </li>
        </ol>
        <p>
          A company may make an election for this order not to apply for an
          accounting period, in which case the company must specify the
          particular tax-interest expense amounts that are to be left out of
          account:
        </p>
        <RoundedBorder>
          <DataEditor
            freezeColumns={1}
            columns={ap_reactivation_table_columns}
            getCellContent={([col, row]) =>
              getAPCellContent((col = col - 1), row)
            }
            rows={ap_reactivation_table_rows?.length}
            rowGrouping={rowGrouping}
            onCellEdited={([col, row], event) =>
              onAPCellEdit((col = col - 1), row, event?.data)
            }
            getRowThemeOverride={(i) =>
              getMetricTheme(ap_reactivation_table_rows[i])
            }
          />
        </RoundedBorder>

        <div className="container-fluid mt-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <BackButton disabled={isPosting} />
            </div>
            <Tooltip
              disabled={!(isError || !validateReactivationAllocation())}
              position="top"
              style={{ maxWidth: "50rem" }}
              content={() =>
                `${isError || "All Remaining Reactivations to be allocated must be = 0"}`
              }
            >
              <Tooltip
                disabled={!(isError || !validateReactivationAllocation())}
                position="top"
                style={{ maxWidth: "50rem" }}
                content={() =>
                  `${isError || "Remaining Reactivations must be 0"}`
                }
              >
                <div className="d-flex gap-2">
                  <Button
                    hiddenInReadonly
                    loading={isPosting}
                    onClick={save}
                    disabled={isError || !validateReactivationAllocation()}
                    kind="secondary"
                  >
                    Save
                  </Button>
                  <NextButton
                    loading={isPosting}
                    preNavigation={save}
                    disabled={
                      isError ||
                      !validateReactivationAllocation() ||
                      disableViewModeProgress
                    }
                  >
                    Next
                  </NextButton>
                </div>
              </Tooltip>
            </Tooltip>
          </div>
        </div>
      </Panel>
    </div>
  );
};

export default ReviewCalc4;
